import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  History,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  User,
  Bot,
  Server,
  FileText,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Clock,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

const ACTION_LABELS = {
  task_created: 'Task Created',
  task_status_changed: 'Task Status Changed',
  content_created: 'Content Created',
  content_updated: 'Content Updated',
  content_published: 'Content Published',
  content_humanized: 'Content Humanized',
  approval_approved: 'Approval Granted',
  approval_rejected: 'Approval Rejected',
  gbp_post_created: 'GBP Post Created',
  gbp_post_updated: 'GBP Post Updated',
  gbp_post_published: 'GBP Post Published',
  gbp_post_humanized: 'GBP Post Humanized',
  onpage_recommendation_approved: 'SEO Fix Approved',
  onpage_recommendation_implemented: 'SEO Fix Implemented',
  cms_push: 'CMS Push',
  cms_auto_execute: 'CMS Auto-Execute',
};

const ACTION_COLORS = {
  task_created: 'bg-blue-100 text-blue-700',
  task_status_changed: 'bg-indigo-100 text-indigo-700',
  content_created: 'bg-emerald-100 text-emerald-700',
  content_updated: 'bg-teal-100 text-teal-700',
  content_published: 'bg-green-100 text-green-700',
  content_humanized: 'bg-violet-100 text-violet-700',
  approval_approved: 'bg-green-100 text-green-700',
  approval_rejected: 'bg-red-100 text-red-700',
  gbp_post_created: 'bg-amber-100 text-amber-700',
  gbp_post_updated: 'bg-orange-100 text-orange-700',
  gbp_post_published: 'bg-lime-100 text-lime-700',
  gbp_post_humanized: 'bg-fuchsia-100 text-fuchsia-700',
  onpage_recommendation_approved: 'bg-cyan-100 text-cyan-700',
  onpage_recommendation_implemented: 'bg-indigo-100 text-indigo-700',
  cms_push: 'bg-purple-100 text-purple-700',
  cms_auto_execute: 'bg-pink-100 text-pink-700',
};

const ACTOR_CONFIG = {
  human: { icon: User, label: 'Human', color: 'bg-blue-50 text-blue-600 border-blue-200' },
  ai: { icon: Bot, label: 'AI', color: 'bg-violet-50 text-violet-600 border-violet-200' },
  system: { icon: Server, label: 'System', color: 'bg-gray-50 text-gray-600 border-gray-200' },
};

function formatTimestamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function SnapshotDiff({ before, after }) {
  if (!before && !after) return null;
  const allKeys = [...new Set([...Object.keys(before || {}), ...Object.keys(after || {})])];
  if (allKeys.length === 0) return null;

  return (
    <div className="mt-3 rounded-lg border border-gray-200 overflow-hidden text-sm">
      <div className="grid grid-cols-[1fr_auto_1fr] bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-500 border-b">
        <span>Before</span>
        <span></span>
        <span>After</span>
      </div>
      {allKeys.map((key) => {
        const bVal = before?.[key];
        const aVal = after?.[key];
        const changed = JSON.stringify(bVal) !== JSON.stringify(aVal);
        return (
          <div
            key={key}
            className={`grid grid-cols-[1fr_auto_1fr] px-3 py-1.5 border-b last:border-b-0 ${
              changed ? 'bg-yellow-50/50' : ''
            }`}
          >
            <span className="text-gray-500 truncate">
              <span className="font-medium text-gray-700">{key}:</span>{' '}
              {bVal !== undefined ? String(bVal) : '—'}
            </span>
            <ArrowRight size={14} className="mx-2 text-gray-300 self-center" />
            <span className={changed ? 'text-indigo-700 font-medium truncate' : 'text-gray-500 truncate'}>
              <span className="font-medium text-gray-700">{key}:</span>{' '}
              {aVal !== undefined ? String(aVal) : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function LogEntry({ log }) {
  const [expanded, setExpanded] = useState(false);
  const actorCfg = ACTOR_CONFIG[log.actor] || ACTOR_CONFIG.system;
  const ActorIcon = actorCfg.icon;
  const hasDiff = log.before || log.after;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative pl-8"
    >
      {/* Timeline dot */}
      <div className="absolute left-0 top-3 w-4 h-4 rounded-full border-2 border-indigo-400 bg-white z-10" />

      <Card className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge className={`text-xs ${ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-700'}`}>
                  {ACTION_LABELS[log.action] || log.action}
                </Badge>
                <Badge variant="outline" className={`text-xs ${actorCfg.color}`}>
                  <ActorIcon size={12} className="mr-1" />
                  {actorCfg.label}
                </Badge>
                {log.entity_type && (
                  <span className="text-xs text-gray-400">{log.entity_type}</span>
                )}
              </div>
              <h4 className="font-medium text-gray-900 text-sm">{log.title}</h4>
              {log.description && (
                <p className="text-xs text-gray-500 mt-0.5">{log.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-gray-400 whitespace-nowrap">
                <Clock size={12} className="inline mr-1" />
                {formatTimestamp(log.timestamp)}
              </span>
              {hasDiff && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </Button>
              )}
            </div>
          </div>

          <AnimatePresence>
            {expanded && hasDiff && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <SnapshotDiff before={log.before} after={log.after} />
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function ExecutionLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [pageSize, setPageSize] = useState(30);

  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('page_size', pageSize);
      if (search) params.set('search', search);
      if (actionFilter) params.set('action', actionFilter);
      if (actorFilter) params.set('actor', actorFilter);

      const res = await api.get(`/execution-logs?${params.toString()}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setHasMore(res.data.has_more || false);
    } catch {
      toast.error('Failed to load execution logs');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, actionFilter, actorFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    setPage(1);
  }, [search, actionFilter, actorFilter]);

  const totalPages = Math.ceil(total / pageSize) || 1;

  const actionOptions = Object.entries(ACTION_LABELS);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <History className="text-indigo-600" size={28} />
            Execution Log
          </h1>
          <p className="text-gray-500 mt-1">
            Immutable audit trail of every action — AI and human
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchLogs} disabled={loading}>
          <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Search + Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by title..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Button
              variant={showFilters ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={14} className="mr-1" />
              Filters
              {(actionFilter || actorFilter) && (
                <Badge className="ml-1 bg-indigo-600 text-white text-xs px-1">
                  {[actionFilter, actorFilter].filter(Boolean).length}
                </Badge>
              )}
            </Button>
          </div>

          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 pt-3 border-t">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Action Type</label>
                    <select
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={actionFilter}
                      onChange={(e) => setActionFilter(e.target.value)}
                    >
                      <option value="">All Actions</option>
                      {actionOptions.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Actor</label>
                    <select
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={actorFilter}
                      onChange={(e) => setActorFilter(e.target.value)}
                    >
                      <option value="">All Actors</option>
                      <option value="human">Human</option>
                      <option value="ai">AI</option>
                      <option value="system">System</option>
                    </select>
                  </div>
                </div>
                {(actionFilter || actorFilter) && (
                  <div className="mt-2 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setActionFilter(''); setActorFilter(''); }}
                    >
                      Clear filters
                    </Button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-gray-500">
        <span>{total} log {total === 1 ? 'entry' : 'entries'}</span>
        <span className="text-gray-300">|</span>
        <span>Page {page} of {totalPages}</span>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="space-y-4 pl-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="relative">
              <div className="absolute -left-8 top-3 w-4 h-4 rounded-full bg-gray-200 animate-pulse" />
              <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-2">
                <div className="flex gap-2">
                  <div className="h-5 w-24 bg-gray-200 rounded animate-pulse" />
                  <div className="h-5 w-16 bg-gray-200 rounded animate-pulse" />
                </div>
                <div className="h-4 w-3/4 bg-gray-100 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText size={48} className="mx-auto text-gray-300 mb-3" />
            <h3 className="text-lg font-medium text-gray-700">No log entries found</h3>
            <p className="text-gray-500 mt-1 text-sm">
              {search || actionFilter || actorFilter
                ? 'Try adjusting your filters'
                : 'Actions will appear here as your team works'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="relative">
          {/* Vertical timeline line */}
          <div className="absolute left-[7px] top-0 bottom-0 w-0.5 bg-indigo-200" />

          <div className="space-y-3">
            {logs.map((log) => (
              <LogEntry key={log.log_id} log={log} />
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {total > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span>
                  Showing {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} of {total}
                </span>
                <span className="text-gray-300">|</span>
                <div className="flex items-center gap-1.5">
                  <label className="text-xs text-gray-500">Per page:</label>
                  <select
                    className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {[20, 30, 50, 100].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(1)}
                  className="h-8 w-8 p-0"
                  title="First page"
                >
                  <ChevronLeft size={14} />
                  <ChevronLeft size={14} className="-ml-2" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="h-8 w-8 p-0"
                  title="Previous page"
                >
                  <ChevronLeft size={14} />
                </Button>

                {(() => {
                  const pages = [];
                  let start = Math.max(1, page - 2);
                  let end = Math.min(totalPages, start + 4);
                  if (end - start < 4) start = Math.max(1, end - 4);
                  for (let i = start; i <= end; i++) pages.push(i);
                  return pages.map((p) => (
                    <Button
                      key={p}
                      variant={p === page ? 'default' : 'outline'}
                      size="sm"
                      className="h-8 w-8 p-0 text-xs"
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </Button>
                  ));
                })()}

                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasMore}
                  onClick={() => setPage((p) => p + 1)}
                  className="h-8 w-8 p-0"
                  title="Next page"
                >
                  <ChevronRight size={14} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(totalPages)}
                  className="h-8 w-8 p-0"
                  title="Last page"
                >
                  <ChevronRight size={14} />
                  <ChevronRight size={14} className="-ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
